// src/services/api.ts
import axios, { AxiosError, type AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import { store } from '@/app/store';
import { handleLogout } from '@/features/auth/utils/authUtils';
import { readApiError, readBlobBody, getFieldErrors, DEFAULT_ERROR_MESSAGE } from './apiError';
import type {
    ApiErrorEnvelope,
    ApiErrorResponse,
    ApiErrorType,
    NormalisedApiError,
} from './apiError';

declare module 'axios' {
    interface AxiosRequestConfig {
        suppressGlobalForbiddenToast?: boolean;
    }
}

// Re-exported so consumers keep one import path (`@/services/api`) for both the
// axios instance and the error-shape helpers. apiError.ts is the source of truth
// for the wire shape; nothing here re-parses a response body.
export { readApiError, getFieldErrors, DEFAULT_ERROR_MESSAGE };
export type { ApiErrorEnvelope, ApiErrorResponse, ApiErrorType, NormalisedApiError };

// Enhanced error interface
export interface ApiError {
    message: string;
    response?: AxiosResponse;
    status?: number;
    data?: ApiErrorResponse;
    originalError: AxiosError;
    type?: ApiErrorType | string;
    code?: string;
    param?: string;
}

// Type guard to check if an error is an ApiError
export const isApiError = (error: unknown): error is ApiError => {
    return (
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        'originalError' in error &&
        'status' in error
    );
};

// Helper to check specific error status
export const isValidationError = (error: unknown): boolean => {
    return isApiError(error) && error.status === 422;
};

export const isNotFoundError = (error: unknown): boolean => {
    return isApiError(error) && error.status === 404;
};

const SENSITIVE_DATA_KEYS =
    /^(password|password_confirmation|current_password|token|secret|api_key)$/i;
const REDACTED = '[REDACTED]';

function redactSensitiveData(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(redactSensitiveData);
    }
    if (value !== null && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, val]) => [
                key,
                SENSITIVE_DATA_KEYS.test(key) ? REDACTED : redactSensitiveData(val),
            ])
        );
    }
    return value;
}

// config.data is already a JSON string by the time a response interceptor
// runs (dispatchRequest's transformRequest serialises it); parse to redact.
function redactRequestBody(data: unknown): unknown {
    if (typeof data !== 'string') {
        return typeof data === 'object' && data !== null ? redactSensitiveData(data) : data;
    }
    try {
        return JSON.stringify(redactSensitiveData(JSON.parse(data)));
    } catch {
        return data;
    }
}

const api = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost/api',
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Automatically attach token to every request except public routes
const PUBLIC_PATHS = ['/login'];

api.interceptors.request.use(
    (config) => {
        const token = store.getState().auth.token;
        const isPublic = PUBLIC_PATHS.some((path) => config.url?.includes(path));

        if (!isPublic && token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Handle errors uniformly
api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError): Promise<ApiError> => {
        // Blob error bodies are read first, so the logout and toast decisions below see the real code.
        const body = await readBlobBody(error.response?.data);
        const normalised = readApiError(body);

        // Never widen to type === 'authentication_error' — a wrong password maps to it
        // too, and widening would log the user out mid-login.
        if (error.response?.status === 401 && normalised.code === 'invalid_token') {
            // Dispatch logout action
            handleLogout(store.dispatch);
        }

        // These refusals are answered by their own prompt or modal, not a permission toast.
        if (
            error.response?.status === 403 &&
            !error.config?.suppressGlobalForbiddenToast &&
            normalised.code !== 'access_justification_required' &&
            normalised.code !== 'dealer_product_not_authorised'
        ) {
            toast.error("You don't have permission to perform this action.", {
                toastId: 'forbidden',
            });
        }

        // originalError.config and response.config are the same object, so one
        // delete closes both routes the token could take out of this rejection.
        error.config?.headers?.delete?.('Authorization');

        if (error.config) {
            error.config.data = redactRequestBody(error.config.data);
        }

        // Create structured error that preserves response data
        const enhancedError: ApiError = {
            message: normalised.message,
            response: error.response,
            status: error.response?.status,
            data: body as ApiErrorResponse | undefined,
            originalError: error,
            type: normalised.type,
            code: normalised.code,
            param: normalised.param,
        };

        return Promise.reject(enhancedError);
    }
);

export default api;
